import requestsService from '~/services/requestsService';
import { User } from '~/hooks/useManagementAuth';
import { getPlatform } from '~/utils/client-utils';

const basePath = 'auth' as const;

/**
 * Get the current authenticated user with roles and permissions
 */
async function getCurrentUser(): Promise<User> {
  const response = await requestsService.fetch<User>(`${basePath}/profile`);
  if (!response) {
    throw new Error('Failed to fetch user profile');
  }
  return response;
}

/**
 * Get authentication platform
 * Returns the current platform as detected by the application
 */
function getAuthPlatform(): string {
  return getPlatform();
}

/**
 * Get user permissions
 */
async function getUserPermissions(): Promise<{
  roles: string[];
  permissions: string[];
}> {
  const response = await requestsService.fetch<{
    roles: string[];
    permissions: string[];
  }>(`${basePath}/permissions`);
  if (!response) {
    return { roles: [], permissions: [] };
  }
  return response;
}

/**
 * Logout the current user
 */
async function logout(): Promise<void> {
  return requestsService.post<void>(`${basePath}/logout`);
}

/**
 * Check if user has specific permission
 */
async function checkPermission(permission: string): Promise<boolean> {
  try {
    const response = await requestsService.fetch<{ hasPermission: boolean }>(
      `${basePath}/check-permission`,
      {
        params: { permission },
      },
    );
    return response?.hasPermission || false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

/**
 * Refresh the authentication token
 * @returns A new token with extended expiration
 */
async function refreshToken(): Promise<{ token: string }> {
  try {
    const response = await requestsService.post<{ token: string }>(
      `${basePath}/refresh-token`,
    );

    // The token is also set as a cookie by the backend
    return response;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
}

const authService = {
  getCurrentUser,
  getUserPermissions,
  logout,
  checkPermission,
  refreshToken,
  getAuthPlatform,
};

export default authService;
